<template>
  <div class="input-group session-status-widget">
    <span class="input-group-text">Status</span>
    <div class="form-control status-indicator d-flex align-items-center justify-content-center">
      <i :class="['fas', 'fa-circle', statusClass, { 'pulse': isValid }]"></i>
      <span class="ms-2 countdown-timer">{{ countdown }}</span>
    </div>
  </div>
</template>

<script>
// Import axios directly
import axios from 'axios'

export default {
  data() {
    return {
      isValid: true,
      checkInterval: null,
      countdownInterval: null,
      lastChecked: null,
      checkFrequency: 60, // Check every 60 seconds (1 minute)
      countdown: 60, // Countdown timer in seconds
      sessionTokens: {
        dbToken: null,
        sessionToken: null
      }
    }
  },
  
  computed: {
    statusClass() {
      return this.isValid ? 'text-success' : 'text-danger';
    }
  },
  
  mounted() {
    // Start checking session status periodically
    this.startChecking();
    
    // Also check when tab becomes visible again
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  
  beforeUnmount() {
    // Clean up
    this.stopChecking();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  
  methods: {
    startChecking() {
      console.log('Starting session status checks');
      // Clear any existing intervals
      this.stopChecking();
      
      // Reset countdown
      this.countdown = this.checkFrequency;
      
      // Set up interval for checking session status
      this.checkInterval = setInterval(() => {
        this.checkSessionStatus();
        // Reset countdown after each check
        this.countdown = this.checkFrequency;
      }, this.checkFrequency * 1000); // Convert seconds to milliseconds
      
      // Set up interval for countdown timer
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1;
        }
      }, 1000); // Update every second
      
      // Also check immediately on start
      this.checkSessionStatus();
    },
    
    stopChecking() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    
    handleVisibilityChange() {
      // Check session when user comes back to the tab
      if (document.visibilityState === 'visible') {
        console.log('Tab visible again, checking session status');
        this.checkSessionStatus();
      }
    },
    
    async checkSessionStatus() {
      try {
        console.log('Checking session status...');
        
        // Get CSRF token for requests
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (token) {
          axios.defaults.headers.common['X-CSRF-Token'] = token;
        }
        
        const response = await axios.get('/users/session/status');
        this.lastChecked = new Date();
        
        // Update status
        if (response.data.authenticated) {
          // Store token information
          if (response.data.debug) {
            this.sessionTokens.dbToken = response.data.debug.db_token;
            this.sessionTokens.sessionToken = response.data.debug.session_token;
            
            // Log token comparison for debugging
            console.log('Token comparison:', {
              dbToken: this.sessionTokens.dbToken,
              sessionToken: this.sessionTokens.sessionToken,
              match: response.data.debug.tokens_match
            });
          }
          
          // For exempt users or valid session
          if (response.data.exempt_from_restriction || response.data.session_valid) {
            this.isValid = true;
          } else {
            this.isValid = false;
            console.warn('Session is invalid - tokens don\'t match. Will refresh page.');
            
            // Add a small delay before refreshing
            setTimeout(() => {
              console.log('Refreshing page due to token mismatch...');
              window.location.reload();
            }, 2000); // 2-second delay to allow user to see status change
          }
        } else {
          this.isValid = false;
          console.warn('Not authenticated - will refresh page');
          
          // Force refresh for unauthenticated user
          setTimeout(() => {
            console.log('Refreshing page due to authentication loss...');
            window.location.reload();
          }, 2000); // 2-second delay
        }
      } catch (error) {
        console.error('Error checking session status:', error);
        
        // Check if this is a 401 Unauthorized error
        if (error.response && error.response.status === 401) {
          console.warn('401 Unauthorized response - User is not authenticated');
          this.isValid = false;
          
          // Refresh the page which will redirect to login
          setTimeout(() => {
            console.log('Refreshing page due to 401 Unauthorized...');
            window.location.reload();
          }, 1000);
        } else {
          // For other errors, don't change the status
          console.warn('Non-auth related error:', error.message);
        }
      }
    }
  }
}
</script>

<style>
.session-status-widget {
  max-width: 180px;
  margin-bottom: 0 !important;
}

.status-indicator {
  min-width: 80px;
  height: 38px;
  font-size: 16px;
  padding: 0.375rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-group-text {
  min-width: 80px;
  justify-content: center;
}

/* Countdown timer styling */
.countdown-timer {
  font-size: 12px;
  font-family: monospace;
  color: #6c757d;
  margin-left: 8px;
  min-width: 24px;
  text-align: right;
}

/* Pulse animation for the status dot */
@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.pulse {
  animation: pulse 2s infinite;
}

.fa-circle {
  font-size: 18px;
}
</style>
